import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Camera, CameraOff, Flashlight, Keyboard, PackageSearch, MinusCircle, PlusCircle,
  ArrowLeftRight, ClipboardCheck, Send, Eye, MapPin, RefreshCw, ScanBarcode, Bluetooth,
  Pencil, ChevronLeft, Radio, Trash2, Plus, Minus, Check, AlertTriangle, Truck, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { inv, INV_API } from '../../lib/invApi';
import { normalizeBarcode, describeScan } from '../../lib/normalizeBarcode';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 1D symbologies our numeric NexFlow labels use (Code 128 / Code 39) plus common
// retail formats, so both camera + USB/BT resolve the SAME registry value.
const ZXING_FORMATS = [
  BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93,
  BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
  BarcodeFormat.ITF, BarcodeFormat.CODABAR, BarcodeFormat.QR_CODE,
];
const NATIVE_FORMATS = ['code_128', 'code_39', 'code_93', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'itf', 'codabar', 'qr_code'];

// From multiple detected barcodes, prefer the one whose center is closest to the
// video center (i.e. inside the guidance scan-box) so a sheet of many labels
// resolves the CENTERED one instead of firing randomly.
function pickCentered(codes, video) {
  if (!codes || !codes.length) return null;
  if (codes.length === 1) return codes[0].rawValue;
  const cx = (video?.videoWidth || 0) / 2;
  const cy = (video?.videoHeight || 0) / 2;
  let best = null; let bestD = Infinity;
  for (const c of codes) {
    const b = c.boundingBox || {};
    const bx = (b.x ?? 0) + (b.width ?? 0) / 2;
    const by = (b.y ?? 0) + (b.height ?? 0) / 2;
    const d = Math.hypot(bx - cx, by - cy);
    if (d < bestD) { bestD = d; best = c; }
  }
  return best ? best.rawValue : codes[0].rawValue;
}

// mode -> default action & the visible action buttons
const MODE_CONFIG = {
  // Field material scan-out is not part of the current process (Use Material hidden; backend/history kept).
  quick_use:      { title: 'Lookup',          defaultAction: null },
  job_use:        { title: 'Lookup',          defaultAction: null },
  van_count:      { title: 'Van Count',        defaultAction: 'count' },
  restock_van:    { title: 'Restock Van',      defaultAction: 'receive' },
  transfer:       { title: 'Transfer',         defaultAction: 'transfer' },
  request_restock:{ title: 'Request Restock',  defaultAction: 'request_restock' },
  lookup:         { title: 'Lookup',           defaultAction: null },
  scan:           { title: 'Scan Barcode',     defaultAction: null },
};

const ACTIONS = [
  { key: 'receive',         label: 'Restock / Receive', icon: PlusCircle,   color: 'text-green-400' },
  { key: 'transfer',        label: 'Transfer',        icon: ArrowLeftRight, color: 'text-blue-400' },
  { key: 'count',           label: 'Count / Adjust',  icon: ClipboardCheck, color: 'text-amber-400' },
  { key: 'request_restock', label: 'Request Restock', icon: Send,           color: 'text-[#D4AF37]' },
  { key: 'lookup',          label: 'View Only',       icon: Eye,            color: 'text-[#A3A3A3]' },
];

// Rapid-session (Bluetooth / USB) action config — each declares its location prompt(s).
// primaryLabel = the single location the action operates on (source or destination),
// needsTo = a transfer with a second location. lookup accumulates nothing.
const EXT_ACTIONS = [
  { key: 'receive',         label: 'Restock / Receive', icon: PlusCircle,     color: 'text-green-400',  verb: 'Into',        primaryLabel: 'Receiving into',               needsTo: false, defaultsToVan: true },
  { key: 'count',           label: 'Count / Adjust',    icon: ClipboardCheck, color: 'text-amber-400',  verb: 'Location',    primaryLabel: 'Which location are you counting', needsTo: false, defaultsToVan: true },
  { key: 'transfer',        label: 'Transfer',          icon: ArrowLeftRight, color: 'text-blue-400',   verb: '',            primaryLabel: 'From location',                needsTo: true,  defaultsToVan: false },
  { key: 'request_restock', label: 'Request Restock',   icon: Send,           color: 'text-[#D4AF37]',  verb: 'For',         primaryLabel: 'Which location needs restocked', needsTo: false, defaultsToVan: true },
  { key: 'lookup',          label: 'View Only',         icon: Eye,            color: 'text-[#A3A3A3]',  verb: '',            primaryLabel: '',                             needsTo: false, defaultsToVan: false },
];
const extActionCfg = (k) => EXT_ACTIONS.find((a) => a.key === k) || EXT_ACTIONS.find((a) => a.key === 'lookup');

// Map the saved per-user preference to the initial scanner surface.
const initialSourceFor = (pref) => (pref === 'camera' ? 'camera' : pref === 'external' ? 'external' : 'chooser');

const BarcodeScanner = ({ open, onClose, mode = 'scan', context = {}, onDone }) => {
  const cfg = MODE_CONFIG[mode] || MODE_CONFIG.scan;
  const { user, setUser } = useAuth();
  const scannerPref = (user && typeof user === 'object' && user.scanner_preference) || 'ask';

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const detectorRef = useRef(null);
  const rafRef = useRef(null);
  const startingRef = useRef(false);
  const hintTimerRef = useRef(null);
  const lastScanRef = useRef({ code: '', at: 0 });
  const stableRef = useRef({ code: '', count: 0 });
  const wedgeRef = useRef({ buf: '', last: 0 });
  const extInputRef = useRef(null);

  // 'chooser' | 'camera' | 'external' | 'manual'
  const [source, setSource] = useState('chooser');
  const [phase, setPhase] = useState('scan'); // scan | resolved
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('starting'); // starting | scanning | detected | looking | found | idle
  const [detectedCode, setDetectedCode] = useState('');
  const [noHitHint, setNoHitHint] = useState(false);
  const [lastScanDiag, setLastScanDiag] = useState(null);
  const [savingPref, setSavingPref] = useState(false);

  const [locations, setLocations] = useState([]);
  const [myLoc, setMyLoc] = useState(null);
  const [result, setResult] = useState(null); // lookup response

  const [action, setAction] = useState(cfg.defaultAction);
  const [locId, setLocId] = useState(context.location_id || '');
  const [toLocId, setToLocId] = useState('');
  const [qty, setQty] = useState('1');
  const [counted, setCounted] = useState('');
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');

  // ---- Rapid Bluetooth / USB session (Action-First) ----
  // extStep: 'action' -> 'setup' -> 'scanning' -> 'review'
  const [extStep, setExtStep] = useState('action');
  const [extAction, setExtAction] = useState(null);
  const [extFromLoc, setExtFromLoc] = useState('');
  const [extToLoc, setExtToLoc] = useState('');
  const [extLines, setExtLines] = useState([]); // [{barcode,item_id,name,size,photo_file_id,qty,system_qty}]
  const [extScanBuf, setExtScanBuf] = useState('');
  const [extTotalScans, setExtTotalScans] = useState(0);
  const [extLastHit, setExtLastHit] = useState(null); // {name, qty} — drives the +1 pulse
  const [extPulse, setExtPulse] = useState(0);
  const [extBusy, setExtBusy] = useState(false);
  const [extLookupItem, setExtLookupItem] = useState(null); // View Only current item
  const [extConfirmChange, setExtConfirmChange] = useState(false);
  const extScanInputRef = useRef(null);
  const extLookupCache = useRef({}); // normalized code -> lookup response
  const extLinesRef = useRef([]);    // keep latest lines for the async scan handler
  extLinesRef.current = extLines;

  const stopCamera = useCallback(async () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (hintTimerRef.current) { clearTimeout(hintTimerRef.current); hintTimerRef.current = null; }
    detectorRef.current = null;
    const ctrl = zxingControlsRef.current;
    zxingControlsRef.current = null;
    if (ctrl) { try { ctrl.stop(); } catch { /* */ } }
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* */ } }
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch { /* */ } }
    setCameraOn(false);
    setTorchOn(false);
    setZoomSupported(false);
    setZoom(1);
  }, []);

  const focusExt = useCallback(() => {
    // Keep the HID capture input focused so a Bluetooth/USB keyboard-wedge scan
    // is captured. Deferred a tick so it wins after render/animation.
    setTimeout(() => { try { extInputRef.current?.focus(); } catch { /* */ } }, 60);
  }, []);

  const resolveCode = useCallback(async (rawCode, opts = {}) => {
    // SINGLE shared normalization for every input path (camera / manual / external
    // HID wedge). Strips symbology prefixes + control/zero-width noise, preserving
    // the real digits — then hits the ONE canonical lookup.
    const rawForDiag = opts.raw != null ? opts.raw : rawCode;
    const diag = describeScan(rawForDiag);
    setLastScanDiag(diag);
    try {
      // eslint-disable-next-line no-console
      console.debug('[barcode] source=%s raw=%s codes=%o -> normalized=%s',
        opts.fromCamera ? 'camera' : (opts.fromWedge ? 'external' : 'manual'),
        diag.rawJson, diag.rawCharCodes, diag.normalized);
    } catch { /* */ }
    const code = diag.normalized;
    if (!code) return;
    const now = Date.now();
    // Debounce identical reads (duplicate-frame / CR+LF double-terminator protection).
    if (lastScanRef.current.code === code && now - lastScanRef.current.at < 2500) return;
    lastScanRef.current = { code, at: now };
    setBusy(true);
    setStatus('looking');
    setDetectedCode(code);
    if (opts.fromCamera && navigator.vibrate) { try { navigator.vibrate(60); } catch { /* */ } }
    try {
      const { data } = await inv.get('/barcodes/lookup', { code });
      if (!data.found) {
        toast.error(`Barcode ${code} not found`);
        setStatus('scanning');
        setBusy(false);
        stableRef.current = { code: '', count: 0 };
        if (opts.fromWedge) focusExt();
        return;
      }
      await stopCamera();
      setStatus('found');
      setResult(data);
      setPhase('resolved');
      let pre = context.location_id || (myLoc?.reliable ? myLoc.location.id : '');
      if (!pre && (data.stock || []).length === 1) pre = data.stock[0].location_id;
      setLocId(pre || '');
      setAction(cfg.defaultAction);
      const cnt = (data.stock || []).find((r) => r.location_id === pre);
      setCounted(cnt ? String(cnt.quantity) : '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
      setStatus('scanning');
    }
    setBusy(false);
  }, [context.location_id, myLoc, stopCamera, cfg.defaultAction, focusExt]);

  const handleCandidate = useCallback((raw) => {
    const code = normalizeBarcode(raw);
    if (!code) return;
    if (stableRef.current.code === code) stableRef.current.count += 1;
    else stableRef.current = { code, count: 1 };
    if (stableRef.current.count >= 2) {
      setNoHitHint(false);
      setStatus('detected');
      resolveCode(code, { fromCamera: true, raw });
    }
  }, [resolveCode]);

  const startCamera = useCallback(async () => {
    if (startingRef.current || streamRef.current) return;
    startingRef.current = true;
    setCameraError(null);
    setNoHitHint(false);
    setStatus('starting');
    stableRef.current = { code: '', count: 0 };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 }, height: { ideal: 1080 },
          advanced: [{ focusMode: 'continuous' }],
        },
        audio: false,
      });
      streamRef.current = stream;
      let video = videoRef.current;
      if (!video) {
        await new Promise((r) => requestAnimationFrame(() => r()));
        video = videoRef.current;
      }
      if (!video) { throw new Error('Video element not ready'); }
      video.setAttribute('playsinline', 'true');
      video.setAttribute('muted', 'true');
      video.srcObject = stream;
      await video.play().catch(() => {});
      setCameraOn(true);
      setStatus('scanning');
      try {
        const track = stream.getVideoTracks()[0];
        const caps = track.getCapabilities ? track.getCapabilities() : {};
        setTorchSupported(!!caps.torch);
        if (caps.zoom && (caps.zoom.max || 0) > (caps.zoom.min || 1)) {
          setZoomRange({ min: caps.zoom.min ?? 1, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
          setZoomSupported(true);
          const settings = track.getSettings ? track.getSettings() : {};
          setZoom(settings.zoom || caps.zoom.min || 1);
        } else {
          setZoomSupported(false);
        }
        if (caps.focusMode && caps.focusMode.includes('continuous')) {
          try { await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }); } catch { /* */ }
        }
      } catch { setTorchSupported(false); setZoomSupported(false); }

      let usedNative = false;
      if ('BarcodeDetector' in window) {
        try {
          let supported = null;
          try { supported = await window.BarcodeDetector.getSupportedFormats(); } catch { supported = null; }
          const want = NATIVE_FORMATS.filter((f) => !supported || supported.includes(f));
          if (want.length) {
            detectorRef.current = new window.BarcodeDetector({ formats: want });
            usedNative = true;
            let lastTick = 0;
            const loop = async (ts) => {
              if (!detectorRef.current || !videoRef.current) return;
              if (ts - lastTick >= 120) {
                lastTick = ts;
                try {
                  const codes = await detectorRef.current.detect(videoRef.current);
                  const best = pickCentered(codes, videoRef.current);
                  if (best) handleCandidate(best);
                } catch { /* frame not ready */ }
              }
              rafRef.current = requestAnimationFrame(loop);
            };
            rafRef.current = requestAnimationFrame(loop);
          }
        } catch { usedNative = false; }
      }
      if (!usedNative) {
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 120 });
        zxingControlsRef.current = await reader.decodeFromVideoElement(video, (res) => {
          if (res) handleCandidate(res.getText());
        });
      }
      hintTimerRef.current = setTimeout(() => setNoHitHint(true), 6000);
    } catch (e) {
      streamRef.current = null;
      const msg = /denied|permission/i.test(e?.message || '') ? 'Camera permission denied' : (e?.message || 'Unable to access camera');
      setCameraError(msg);
      setCameraOn(false);
      setStatus('idle');
    }
    startingRef.current = false;
  }, [handleCandidate]);

  const toggleTorch = async () => {
    const stream = streamRef.current;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch { toast.error('Flashlight not supported on this device'); }
  };

  const applyZoom = async (next) => {
    const stream = streamRef.current;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    const z = Math.min(zoomRange.max, Math.max(zoomRange.min, next));
    try {
      await track.applyConstraints({ advanced: [{ zoom: z }] });
      setZoom(z);
    } catch { /* zoom unsupported mid-stream */ }
  };

  const tapFocus = async (e) => {
    const stream = streamRef.current;
    const video = videoRef.current;
    if (!stream || !video) return;
    const track = stream.getVideoTracks()[0];
    const caps = track.getCapabilities ? track.getCapabilities() : {};
    if (!caps || (!caps.focusMode && !caps.pointsOfInterest)) return;
    try {
      const rect = video.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
      const adv = {};
      if (caps.pointsOfInterest) adv.pointsOfInterest = [{ x, y }];
      if (caps.focusMode?.includes('single-shot')) adv.focusMode = 'single-shot';
      else if (caps.focusMode?.includes('manual')) adv.focusMode = 'continuous';
      await track.applyConstraints({ advanced: [adv] });
      if (adv.focusMode === 'single-shot' && caps.focusMode?.includes('continuous')) {
        setTimeout(() => { track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {}); }, 1200);
      }
    } catch { /* focus control unavailable */ }
  };

  // On open: reset state + pick the initial surface from the saved preference.
  // Locations load always; camera is started only by the source effect below.
  useEffect(() => {
    if (!open) return;
    setPhase('scan'); setResult(null); setManual(''); setCameraError(null);
    setLastScanDiag(null);
    setAction(cfg.defaultAction); setQty('1'); setCounted(''); setNotes(''); setPriority('normal');
    setLocId(context.location_id || ''); setToLocId('');
    lastScanRef.current = { code: '', at: 0 };
    setSource(initialSourceFor(scannerPref));
    // Reset rapid Bluetooth/USB session.
    setExtStep('action'); setExtAction(null); setExtFromLoc(''); setExtToLoc('');
    setExtLines([]); setExtScanBuf(''); setExtTotalScans(0); setExtLastHit(null);
    setExtLookupItem(null); setExtConfirmChange(false);
    extLookupCache.current = {}; extLinesRef.current = [];
    Promise.all([
      inv.get('/locations').then(({ data }) => setLocations(data || [])).catch(() => setLocations([])),
      inv.get('/my-location').then(({ data }) => setMyLoc(data)).catch(() => setMyLoc(null)),
    ]);
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Camera lifecycle is driven purely by `source` — it NEVER starts unless the
  // user is in camera mode (this is the fix for "Scan Barcode always opens camera").
  useEffect(() => {
    if (!open) return;
    if (source === 'camera' && phase === 'scan') startCamera();
    else stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source, phase]);

  // Keep the rapid-session HID input focused while scanning so every physical
  // beep is captured. Re-focus whenever we (re)enter the scanning step.
  const focusExtScan = useCallback(() => {
    setTimeout(() => { try { extScanInputRef.current?.focus(); } catch { /* */ } }, 60);
  }, []);
  useEffect(() => {
    if (!open || source !== 'external') return;
    if (extStep === 'scanning') focusExtScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source, extStep]);

  // Camera-mode keyboard-wedge bonus: a BT scan still resolves even if the tech
  // left it on camera. External mode uses its own focused input instead, so this
  // is scoped to camera to avoid double-capturing one physical scan.
  useEffect(() => {
    if (!open || source !== 'camera' || phase !== 'scan') return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const now = Date.now();
      if (now - wedgeRef.current.last > 120) wedgeRef.current.buf = '';
      wedgeRef.current.last = now;
      if (e.key === 'Enter') {
        const b = wedgeRef.current.buf;
        wedgeRef.current.buf = '';
        if (b.length >= 3) { e.preventDefault(); resolveCode(b, { fromWedge: true, raw: b }); }
        return;
      }
      if (e.key.length === 1) {
        if (tag === 'input') return;
        wedgeRef.current.buf += e.key;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, source, phase, resolveCode]);

  const savePreference = async (pref) => {
    setSavingPref(true);
    try {
      await axios.put(`${BACKEND_URL}/api/user/scanner-preference`, { preference: pref }, { withCredentials: true });
      if (setUser && user && typeof user === 'object') setUser({ ...user, scanner_preference: pref });
      toast.success(pref === 'ask' ? 'Will ask every time' : pref === 'camera' ? 'Default: Phone camera' : 'Default: Bluetooth / USB');
    } catch {
      toast.error('Could not save preference');
    }
    setSavingPref(false);
  };

  const close = async () => { await stopCamera(); onClose?.(); };

  // Return to the ready/scan state on the SAME source (continuous scanning).
  const rescan = async () => {
    setPhase('scan'); setResult(null); setManual('');
    lastScanRef.current = { code: '', at: 0 };
    stableRef.current = { code: '', count: 0 };
    if (source === 'camera') await startCamera();
    else if (source === 'external') focusExt();
  };

  const chooseSource = async (next) => {
    await stopCamera();
    setPhase('scan'); setResult(null); setManual('');
    if (next === 'external') {
      // Fresh Action-First session every time we enter Bluetooth/USB mode.
      setExtStep('action'); setExtAction(null); setExtFromLoc(''); setExtToLoc('');
      setExtLines([]); setExtScanBuf(''); setExtTotalScans(0); setExtLastHit(null);
      setExtLookupItem(null); setExtConfirmChange(false);
      extLookupCache.current = {};
    }
    setSource(next);
  };

  // ---- Rapid Bluetooth / USB session helpers ----
  const myVanLoc = myLoc?.reliable ? myLoc.location : null;

  const extResetSession = () => {
    setExtStep('action'); setExtAction(null); setExtFromLoc(''); setExtToLoc('');
    setExtLines([]); setExtScanBuf(''); setExtTotalScans(0); setExtLastHit(null);
    setExtLookupItem(null); setExtConfirmChange(false);
    extLookupCache.current = {};
  };

  // Pick the action, then pre-fill smart defaults from the employee's assigned van.
  const extChooseAction = (key) => {
    setExtAction(key);
    const van = myLoc?.reliable ? myLoc.location.id : '';
    const cfgA = extActionCfg(key);
    if (key === 'transfer') {
      setExtToLoc(van || '');   // default destination = assigned van
      setExtFromLoc('');        // employee picks the source
    } else if (cfgA.defaultsToVan) {
      setExtFromLoc(van || '');
      setExtToLoc('');
    } else {
      setExtFromLoc(''); setExtToLoc('');
    }
    setExtLines([]); setExtScanBuf(''); setExtTotalScans(0); setExtLastHit(null); setExtLookupItem(null);
    setExtStep(key === 'lookup' ? 'scanning' : 'setup');
  };

  const extBeginScanning = () => {
    if (extAction === 'transfer') {
      if (!extFromLoc) { toast.error('Select the From location'); return; }
      if (!extToLoc) { toast.error('Select the To location'); return; }
      if (extFromLoc === extToLoc) { toast.error('From and To must differ'); return; }
    } else if (extAction !== 'lookup' && !extFromLoc) {
      toast.error('Select a location first'); return;
    }
    setExtStep('scanning');
  };

  // Each physical beep = one accepted scan. Same barcode increments; a new barcode
  // adds a session line. CR/LF double-terminators land as an empty buffer -> ignored.
  const extHandleScan = useCallback(async (rawCode) => {
    const diag = describeScan(rawCode);
    const code = diag.normalized;
    if (!code) { focusExtScan(); return; }
    setExtBusy(true);
    try {
      let data = extLookupCache.current[code];
      if (!data) {
        const resp = await inv.get('/barcodes/lookup', { code });
        data = resp.data;
        if (data?.found) extLookupCache.current[code] = data;
      }
      if (!data || !data.found) { toast.error(`Barcode ${code} not found`); focusExtScan(); setExtBusy(false); return; }
      const it = data.item;
      if (extAction === 'lookup') {
        setExtLookupItem({ ...data });
        setExtTotalScans((n) => n + 1);
        focusExtScan(); setExtBusy(false); return;
      }
      // system on-hand at the operating (source/count) location — for the count delta display
      const sysQty = (data.stock || []).find((s) => s.location_id === extFromLoc);
      const sysNum = sysQty ? Number(sysQty.quantity) : 0;
      setExtLines((prev) => {
        const idx = prev.findIndex((l) => l.item_id === String(it.id));
        let next;
        if (idx >= 0) {
          next = prev.map((l, i) => (i === idx ? { ...l, qty: l.qty + 1 } : l));
        } else {
          next = [...prev, {
            barcode: code, item_id: String(it.id), name: it.name, size: it.size || '',
            sku: it.sku || '', photo_file_id: it.photo_file_id || null, qty: 1,
            system_qty: sysNum,
          }];
        }
        const hit = next.find((l) => l.item_id === String(it.id));
        setExtLastHit({ name: it.name, qty: hit ? hit.qty : 1 });
        return next;
      });
      setExtTotalScans((n) => n + 1);
      setExtPulse((p) => p + 1);
      if (navigator.vibrate) { try { navigator.vibrate(40); } catch { /* */ } }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    }
    setExtBusy(false);
    focusExtScan();
  }, [extAction, extFromLoc, focusExtScan]);

  const extSubmitScan = () => {
    const b = extScanBuf;
    setExtScanBuf('');
    if ((b || '').trim().length >= 1) extHandleScan(b);
    else focusExtScan();
  };

  const extAdjustQty = (itemId, delta) => {
    setExtLines((prev) => prev
      .map((l) => (l.item_id === itemId ? { ...l, qty: Math.max(0, l.qty + delta) } : l))
      .filter((l) => l.qty > 0));
  };
  const extSetQty = (itemId, val) => {
    const n = Math.max(0, Math.floor(Number(val) || 0));
    setExtLines((prev) => prev.map((l) => (l.item_id === itemId ? { ...l, qty: n } : l)));
  };
  const extRemoveLine = (itemId) => setExtLines((prev) => prev.filter((l) => l.item_id !== itemId));

  const extTotalUnits = extLines.reduce((s, l) => s + l.qty, 0);

  const extCommit = async () => {
    const lines = extLines.filter((l) => l.qty > 0);
    if (!lines.length) { toast.error('Nothing to confirm'); return; }
    setExtBusy(true);
    try {
      const body = {
        action: extAction,
        location_id: extFromLoc || null,
        to_location_id: extAction === 'transfer' ? extToLoc : null,
        priority,
        job_id: context.job_id || null,
        appointment_id: context.appointment_id || null,
        customer_id: context.customer_id || null,
        lines: lines.map((l) => (
          extAction === 'count'
            ? { barcode: l.barcode, item_id: l.item_id, counted_qty: l.qty }
            : { barcode: l.barcode, item_id: l.item_id, quantity: l.qty }
        )),
      };
      const { data } = await inv.post('/scan/session/commit', body);
      if (data.failed > 0) {
        toast.warning(`${data.committed} committed · ${data.failed} failed — ${(data.errors[0]?.detail) || ''}`);
      } else {
        toast.success(`${data.committed} item${data.committed === 1 ? '' : 's'} committed`);
      }
      onDone?.(data);
      extResetSession();
      await close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Commit failed');
    }
    setExtBusy(false);
  };

  const extRequestChangeSetup = () => {
    if (extLines.length > 0) { setExtConfirmChange(true); return; }
    extResetSession();
  };

  const extLocName = (id) => (locations.find((l) => l.id === id) || {}).name || '—';
  const extSessionHeader = () => {
    switch (extAction) {
      case 'use': return `USE MATERIAL · From ${extLocName(extFromLoc)}`;
      case 'receive': return `RESTOCK / RECEIVE · Into ${extLocName(extFromLoc)}`;
      case 'count': return `COUNT / ADJUST · ${extLocName(extFromLoc)}`;
      case 'transfer': return `TRANSFER · ${extLocName(extFromLoc)} → ${extLocName(extToLoc)}`;
      case 'request_restock': return `REQUEST RESTOCK · For ${extLocName(extFromLoc)}`;
      case 'lookup': return 'VIEW ONLY';
      default: return '';
    }
  };

  const currentLocName = (locations.find((l) => l.id === locId) || {}).name || '';
  const needsLoc = ['use', 'receive', 'count', 'transfer'].includes(action);
  const locMissing = needsLoc && !locId;

  const submit = async () => {
    if (!result?.item) return;
    if (action === 'lookup') {
      // View-only: in external mode stay ready for the next scan (continuous).
      if (source === 'external') { await rescan(); return; }
      await close(); return;
    }
    if (locMissing) { toast.error('Select the inventory location first'); return; }
    if (action === 'transfer' && !toLocId) { toast.error('Select the destination location'); return; }
    if (action === 'transfer' && toLocId === locId) { toast.error('From and To must differ'); return; }
    setBusy(true);
    try {
      const body = {
        item_id: result.item.id,
        barcode: result.barcode,
        action,
        location_id: needsLoc || action === 'request_restock' ? locId || null : null,
        to_location_id: action === 'transfer' ? toLocId : null,
        quantity: ['use', 'receive', 'transfer', 'request_restock'].includes(action) ? parseFloat(qty || '0') : null,
        counted_qty: action === 'count' ? parseFloat(counted || '0') : null,
        priority,
        notes,
        job_id: context.job_id || null,
        appointment_id: context.appointment_id || null,
        customer_id: context.customer_id || null,
      };
      const { data } = await inv.post('/scan/action', body);
      toast.success(data.message + (data.item_name ? ` — ${data.item_name}` : ''));
      onDone?.(data);
      // Continuous scanning: external mode returns to ready instead of closing.
      if (source === 'external') { await rescan(); }
      else { await close(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
    setBusy(false);
  };

  const item = result?.item;

  // ---- Small shared building blocks ----
  const ManualForm = ({ testid = 'barcode-manual-input' }) => (
    <form onSubmit={(e) => { e.preventDefault(); resolveCode(manual); }} className="flex gap-2 items-end">
      <div className="flex-1 space-y-1">
        <Label className="text-white text-xs">Enter barcode manually</Label>
        <Input data-testid={testid} value={manual} onChange={(e) => setManual(e.target.value)} placeholder="e.g. 10000001" className="bg-[#0A0A0A] border-[#262626] text-white font-mono" autoComplete="off" />
      </div>
      <Button data-testid="barcode-manual-submit" type="submit" disabled={busy || !manual.trim()} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><PackageSearch className="w-4 h-4 mr-1" /> Look up</Button>
    </form>
  );

  const SwitchScannerBar = () => (
    <div className="flex items-center justify-between">
      <button data-testid="scanner-switch" onClick={() => chooseSource('chooser')} className="inline-flex items-center gap-1 text-[#A3A3A3] text-xs hover:text-white">
        <ChevronLeft className="w-3.5 h-3.5" /> Switch scanner
      </button>
      {lastScanDiag && lastScanDiag.changed && (
        <span className="text-[10px] text-amber-300 font-mono">cleaned scanner noise</span>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="barcode-scanner-dialog" className="bg-[#0F0F0F] border-[#262626] text-white max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle className="flex items-center gap-2 text-white">
            <ScanBarcode className="w-5 h-5 text-[#D4AF37]" /> {cfg.title}
          </DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">
            {phase === 'resolved' ? 'Review the item, then confirm or scan the next one.'
              : source === 'chooser' ? 'Choose how you want to scan.'
              : source === 'camera' ? 'Point the camera at a label, or tap to focus.'
              : source === 'external' ? 'Scan with your connected Bluetooth / USB scanner.'
              : 'Type the barcode and look it up.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* ---------- CHOOSER ---------- */}
          {phase === 'scan' && source === 'chooser' && (
            <div className="space-y-3" data-testid="scanner-chooser">
              <button data-testid="scanner-choose-camera" onClick={() => chooseSource('camera')}
                className="w-full flex items-center gap-3 rounded-md border border-[#262626] bg-[#141414] hover:border-[#D4AF37]/50 hover:bg-[#1c1c1c] p-4 text-left transition-colors">
                <Camera className="w-6 h-6 text-[#D4AF37] shrink-0" />
                <span><span className="block text-white font-semibold">Use Phone Camera</span><span className="block text-[#A3A3A3] text-xs">Scan a label with this device's camera.</span></span>
              </button>
              <button data-testid="scanner-choose-external" onClick={() => chooseSource('external')}
                className="w-full flex items-center gap-3 rounded-md border border-[#262626] bg-[#141414] hover:border-blue-400/50 hover:bg-[#1c1c1c] p-4 text-left transition-colors">
                <Bluetooth className="w-6 h-6 text-blue-400 shrink-0" />
                <span><span className="block text-white font-semibold">Use Bluetooth / USB Scanner</span><span className="block text-[#A3A3A3] text-xs">Scan with a connected external scanner (no camera).</span></span>
              </button>
              <button data-testid="scanner-choose-manual" onClick={() => chooseSource('manual')}
                className="w-full flex items-center gap-3 rounded-md border border-[#262626] bg-[#141414] hover:border-white/30 hover:bg-[#1c1c1c] p-4 text-left transition-colors">
                <Pencil className="w-6 h-6 text-[#A3A3A3] shrink-0" />
                <span><span className="block text-white font-semibold">Enter Barcode Manually</span><span className="block text-[#A3A3A3] text-xs">Type the 8-digit NexFlow barcode.</span></span>
              </button>

              <div className="pt-2 border-t border-[#262626] space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Preferred scanner (saved to your account)</Label>
                <Select value={scannerPref} onValueChange={savePreference} disabled={savingPref}>
                  <SelectTrigger data-testid="scanner-pref-select" className="bg-[#0A0A0A] border-[#262626] text-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="ask" className="text-white">Ask every time</SelectItem>
                    <SelectItem value="camera" className="text-white">Phone camera</SelectItem>
                    <SelectItem value="external" className="text-white">Bluetooth / USB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* ---------- CAMERA ---------- */}
          {phase === 'scan' && source === 'camera' && (
            <>
              <SwitchScannerBar />
              <div className="rounded-md overflow-hidden bg-black border border-[#262626] relative">
                <video ref={videoRef} data-testid="barcode-camera" onClick={tapFocus} playsInline muted autoPlay className="w-full min-h-[240px] max-h-[52vh] object-cover bg-black cursor-pointer" />
                {cameraOn && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="w-[84%] h-[46%] border-2 border-[#D4AF37]/80 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.22)]" />
                  </div>
                )}
                {cameraOn && (
                  <div data-testid="barcode-status" className="absolute top-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 text-[11px] font-medium flex items-center gap-1.5">
                    {status === 'detected' || status === 'looking' || status === 'found' ? (
                      <><span className="w-1.5 h-1.5 bg-green-400 rounded-full" /> <span className="text-green-300">{status === 'looking' ? `Barcode detected${detectedCode ? ` ${detectedCode}` : ''} · Looking up item…` : status === 'found' ? 'Item found' : `Barcode detected${detectedCode ? ` ${detectedCode}` : ''}`}</span></>
                    ) : (
                      <><span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-pulse" /> <span className="text-white">Point at the barcode — tap to focus</span></>
                    )}
                  </div>
                )}
                {noHitHint && (status === 'scanning') && (
                  <div data-testid="barcode-hint" className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded bg-black/70 text-[11px] text-amber-200 text-center max-w-[90%]">
                    Hold steady and tap the screen to focus. It doesn&apos;t need to fill the box.
                  </div>
                )}
                {!cameraOn && !cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center text-[#A3A3A3] text-sm">
                    <Camera className="w-4 h-4 mr-2" /> Starting camera…
                  </div>
                )}
                {cameraError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center p-4">
                    <CameraOff className="w-6 h-6 text-red-400" />
                    <p className="text-[#A3A3A3] text-xs">{cameraError}. Allow camera access, or switch to Bluetooth / manual.</p>
                    <Button data-testid="barcode-camera-retry" size="sm" onClick={startCamera} className="bg-[#262626] text-white hover:bg-[#333]"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry camera</Button>
                  </div>
                )}
                {cameraOn && torchSupported && (
                  <button data-testid="barcode-torch" onClick={toggleTorch} className={`absolute bottom-2 right-2 rounded-full p-2 ${torchOn ? 'bg-[#D4AF37] text-black' : 'bg-black/60 text-white'}`}>
                    <Flashlight className="w-4 h-4" />
                  </button>
                )}
                {cameraOn && zoomSupported && (
                  <div data-testid="barcode-zoom" className="absolute bottom-2 left-2 right-14 flex items-center gap-2 bg-black/55 rounded-full px-3 py-1.5">
                    <ScanBarcode className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
                    <input type="range" data-testid="barcode-zoom-slider" min={zoomRange.min} max={zoomRange.max} step={zoomRange.step} value={zoom} onChange={(e) => applyZoom(parseFloat(e.target.value))} className="w-full accent-[#D4AF37] cursor-pointer" />
                    <span className="text-[10px] text-white font-mono shrink-0">{zoom.toFixed(1)}x</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-[#A3A3A3] text-xs">
                <Keyboard className="w-3.5 h-3.5" /> A Bluetooth / USB scan will also work here automatically.
              </div>
              <ManualForm />
            </>
          )}

          {/* ---------- EXTERNAL (Bluetooth / USB) — Action-First Rapid Session ---------- */}
          {source === 'external' && (
            <div className="space-y-4" data-testid="scanner-external">
              <SwitchScannerBar />

              {/* STEP 1 — What are you doing? */}
              {extStep === 'action' && (
                <div className="space-y-3" data-testid="ext-step-action">
                  <p className="text-white font-semibold text-sm flex items-center gap-2"><ListChecks className="w-4 h-4 text-blue-400" /> What are you doing?</p>
                  {myLoc?.vehicle_mapping_missing && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200" data-testid="ext-vehicle-mapping-missing">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>Your vehicle <b>{myLoc.vehicle?.name}</b> isn&apos;t linked to an inventory location, so you&apos;ll pick the location manually.</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {EXT_ACTIONS.map((a) => {
                      const Icon = a.icon;
                      return (
                        <button key={a.key} data-testid={`ext-action-${a.key}`} onClick={() => extChooseAction(a.key)}
                          className="flex items-center gap-2 rounded-md border border-[#262626] bg-[#141414] hover:border-blue-400/50 hover:bg-[#1c1c1c] p-3 text-left transition-colors">
                          <Icon className={`w-5 h-5 shrink-0 ${a.color}`} />
                          <span className="text-white text-sm font-medium">{a.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button data-testid="scanner-ext-use-camera" variant="outline" onClick={() => chooseSource('camera')} className="flex-1 border-[#262626] text-white hover:bg-[#262626]"><Camera className="w-4 h-4 mr-1" /> Use Camera Instead</Button>
                    <Button data-testid="scanner-ext-manual" variant="outline" onClick={() => chooseSource('manual')} className="flex-1 border-[#262626] text-white hover:bg-[#262626]"><Pencil className="w-4 h-4 mr-1" /> Enter Manually</Button>
                  </div>
                </div>
              )}

              {/* STEP 2 — Location setup */}
              {extStep === 'setup' && extAction && (
                <div className="space-y-4" data-testid="ext-step-setup">
                  <button data-testid="ext-back-action" onClick={() => setExtStep('action')} className="inline-flex items-center gap-1 text-[#A3A3A3] text-xs hover:text-white">
                    <ChevronLeft className="w-3.5 h-3.5" /> Change action
                  </button>
                  <div className="flex items-center gap-2">
                    {(() => { const A = extActionCfg(extAction).icon; return <A className={`w-5 h-5 ${extActionCfg(extAction).color}`} />; })()}
                    <span className="text-white font-semibold">{extActionCfg(extAction).label}</span>
                  </div>

                  {extAction === 'transfer' ? (
                    <>
                      <div className="space-y-1">
                        <Label className="text-white text-xs flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D4AF37]" /> From location</Label>
                        <Select value={extFromLoc} onValueChange={setExtFromLoc}>
                          <SelectTrigger data-testid="ext-from-location" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select source…" /></SelectTrigger>
                          <SelectContent className="bg-[#141414] border-[#262626]">
                            {locations.filter((l) => l.id !== extToLoc).map((l) => <SelectItem key={l.id} value={l.id} className="text-white">{l.name} ({l.type})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-white text-xs flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-blue-400" /> To location</Label>
                        <Select value={extToLoc} onValueChange={setExtToLoc}>
                          <SelectTrigger data-testid="ext-to-location" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select destination…" /></SelectTrigger>
                          <SelectContent className="bg-[#141414] border-[#262626]">
                            {locations.filter((l) => l.id !== extFromLoc).map((l) => <SelectItem key={l.id} value={l.id} className="text-white">{l.name} ({l.type})</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {myVanLoc && extToLoc === myVanLoc.id && <p className="text-[10px] text-green-400 flex items-center gap-1"><Truck className="w-3 h-3" /> Defaulted To your assigned van: {myVanLoc.name}</p>}
                      </div>
                      {extFromLoc && extToLoc && extFromLoc === extToLoc && <p className="text-[11px] text-red-400">From and To must be different locations.</p>}
                    </>
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-white text-xs flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D4AF37]" /> {extActionCfg(extAction).primaryLabel}</Label>
                      <Select value={extFromLoc} onValueChange={setExtFromLoc}>
                        <SelectTrigger data-testid="ext-primary-location" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select a location…" /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          {locations.map((l) => <SelectItem key={l.id} value={l.id} className="text-white">{l.name} ({l.type})</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {myVanLoc && extFromLoc === myVanLoc.id && <p className="text-[10px] text-green-400 flex items-center gap-1"><Truck className="w-3 h-3" /> Defaulted to your assigned van: {myVanLoc.name}</p>}
                    </div>
                  )}

                  <Button data-testid="ext-begin-scanning" onClick={extBeginScanning} className="w-full bg-blue-500 text-white hover:bg-blue-400"><Radio className="w-4 h-4 mr-1" /> Start Scanning</Button>
                </div>
              )}

              {/* STEP 3 — Rapid scanning */}
              {extStep === 'scanning' && (
                <div className="space-y-3" data-testid="ext-step-scanning">
                  {/* Sticky session header — always shows the current action + location(s) */}
                  <div className="rounded-md border border-blue-400/40 bg-blue-500/10 px-3 py-2" data-testid="ext-session-header">
                    <p className="text-blue-200 text-xs font-bold uppercase tracking-wider">{extSessionHeader()}</p>
                    {extAction !== 'lookup' && (
                      <p className="text-[#A3A3A3] text-[11px] mt-0.5">{extTotalScans} scan{extTotalScans === 1 ? '' : 's'} · {extLines.length} unique item{extLines.length === 1 ? '' : 's'}</p>
                    )}
                  </div>

                  {/* Ready target + focused HID input */}
                  <button type="button" onClick={focusExtScan} className="w-full rounded-lg border-2 border-dashed border-blue-400/50 bg-blue-500/5 p-5 text-center relative overflow-hidden">
                    <Radio className="w-7 h-7 text-blue-400 mx-auto mb-1 animate-pulse" />
                    <p className="text-white font-semibold text-sm">Ready — scan away</p>
                    <p className="text-[#A3A3A3] text-[11px] mt-0.5">Each beep = 1. Scan the same item again to add another.</p>
                    {extLastHit && (
                      <div key={extPulse} className="ext-pulse mt-2 inline-flex items-center gap-2 rounded-full bg-green-500/15 border border-green-500/40 px-3 py-1" data-testid="ext-last-hit">
                        <span className="text-green-400 font-bold text-xs">+1</span>
                        <span className="text-white text-xs truncate max-w-[220px]">{extLastHit.name}</span>
                        <span className="text-[#D4AF37] font-bold text-xs">×{extLastHit.qty}</span>
                      </div>
                    )}
                  </button>
                  <Input
                    ref={extScanInputRef}
                    data-testid="ext-scan-input"
                    value={extScanBuf}
                    onChange={(e) => setExtScanBuf(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); extSubmitScan(); } }}
                    onBlur={() => { if (source === 'external' && extStep === 'scanning') focusExtScan(); }}
                    placeholder="Waiting for scanner…"
                    autoComplete="off" autoCorrect="off" spellCheck={false}
                    className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-center tracking-widest"
                  />
                  {extBusy && <p className="text-center text-blue-300 text-xs" data-testid="ext-busy">Looking up…</p>}

                  {/* View Only — show the current item card, accumulate nothing */}
                  {extAction === 'lookup' && extLookupItem?.item && (
                    <div className="bg-[#141414] border border-[#262626] rounded-md p-3" data-testid="ext-lookup-card">
                      <div className="flex gap-3">
                        {extLookupItem.item.photo_file_id && (
                          <img src={`${INV_API}/items/${extLookupItem.item.id}/photo?kind=item&v=${extLookupItem.item.photo_file_id}`} alt={extLookupItem.item.name}
                            className="w-16 h-16 shrink-0 rounded-md border border-[#262626] bg-[#0A0A0A] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        )}
                        <div className="min-w-0">
                          <p className="text-white font-semibold" data-testid="ext-lookup-name">{extLookupItem.item.name}</p>
                          <p className="text-[#A3A3A3] text-[11px] font-mono">Barcode {extLookupItem.barcode}{extLookupItem.item.size ? ` · ${extLookupItem.item.size}` : ''}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {(extLookupItem.stock || []).map((s) => (
                              <span key={s.location_id} className="bg-[#0A0A0A] border border-[#262626] rounded-full px-2 py-0.5 text-[10px]">{s.location_name}: <span className="text-[#D4AF37] font-bold">{s.quantity}</span></span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Running session list */}
                  {extAction !== 'lookup' && extLines.length > 0 && (
                    <div className="space-y-1.5 max-h-[28vh] overflow-y-auto" data-testid="ext-session-list">
                      {extLines.map((l) => (
                        <div key={l.item_id} className="flex items-center gap-2 rounded-md border border-[#262626] bg-[#141414] px-2.5 py-1.5">
                          <span className="text-white text-xs truncate flex-1">{l.name}{l.size ? ` · ${l.size}` : ''}</span>
                          <span className="text-[#D4AF37] font-bold text-sm" data-testid={`ext-line-qty-${l.item_id}`}>×{l.qty}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <Button data-testid="ext-change-setup" variant="outline" onClick={extRequestChangeSetup} className="border-[#262626] text-white hover:bg-[#262626] text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Change Action / Location</Button>
                    {extAction === 'lookup' ? (
                      <Button data-testid="ext-lookup-done" onClick={close} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Done</Button>
                    ) : (
                      <Button data-testid="ext-review" onClick={() => setExtStep('review')} disabled={extLines.length === 0} className="flex-1 bg-blue-500 text-white hover:bg-blue-400"><ListChecks className="w-4 h-4 mr-1" /> Review ({extTotalUnits})</Button>
                    )}
                  </div>

                  {extConfirmChange && (
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="ext-change-warning">
                      <p className="text-amber-200 text-xs flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> You have {extTotalUnits} scanned unit(s). Changing the action/location discards this session.</p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setExtConfirmChange(false)} className="border-[#262626] text-white hover:bg-[#262626] text-xs">Keep Scanning</Button>
                        <Button size="sm" onClick={() => { setExtConfirmChange(false); extResetSession(); }} className="bg-red-500 text-white hover:bg-red-400 text-xs" data-testid="ext-discard-confirm">Discard &amp; Change</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4 — Review / Confirm */}
              {extStep === 'review' && (
                <div className="space-y-3" data-testid="ext-step-review">
                  <div className="rounded-md border border-blue-400/40 bg-blue-500/10 px-3 py-2">
                    <p className="text-blue-200 text-xs font-bold uppercase tracking-wider">{extSessionHeader()}</p>
                    <p className="text-[#A3A3A3] text-[11px] mt-0.5">Review before committing — nothing is saved until you Confirm.</p>
                  </div>
                  <div className="space-y-2 max-h-[42vh] overflow-y-auto" data-testid="ext-review-list">
                    {extLines.map((l) => (
                      <div key={l.item_id} className="rounded-md border border-[#262626] bg-[#141414] p-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm flex-1 truncate">{l.name}{l.size ? ` · ${l.size}` : ''}</span>
                          <button data-testid={`ext-remove-${l.item_id}`} onClick={() => extRemoveLine(l.item_id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <button data-testid={`ext-minus-${l.item_id}`} onClick={() => extAdjustQty(l.item_id, -1)} className="rounded-full bg-[#262626] p-1.5 text-white hover:bg-[#333]"><Minus className="w-3.5 h-3.5" /></button>
                          <Input data-testid={`ext-qty-${l.item_id}`} type="number" min="0" value={l.qty} onChange={(e) => extSetQty(l.item_id, e.target.value)} className="w-16 h-8 bg-[#0A0A0A] border-[#262626] text-white text-center" />
                          <button data-testid={`ext-plus-${l.item_id}`} onClick={() => extAdjustQty(l.item_id, 1)} className="rounded-full bg-[#262626] p-1.5 text-white hover:bg-[#333]"><Plus className="w-3.5 h-3.5" /></button>
                          {extAction === 'count' && (
                            <span className="ml-auto text-[11px] text-[#A3A3A3]" data-testid={`ext-count-delta-${l.item_id}`}>
                              System {l.system_qty} → Physical {l.qty} · <span className={l.qty - l.system_qty >= 0 ? 'text-green-400' : 'text-red-400'}>{l.qty - l.system_qty >= 0 ? '+' : ''}{l.qty - l.system_qty}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button data-testid="ext-continue-scanning" variant="outline" onClick={() => setExtStep('scanning')} className="border-[#262626] text-white hover:bg-[#262626]"><ScanBarcode className="w-4 h-4 mr-1" /> Continue Scanning</Button>
                    <Button data-testid="ext-confirm" onClick={extCommit} disabled={extBusy || extLines.length === 0} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Check className="w-4 h-4 mr-1" /> {extBusy ? 'Saving…' : `Confirm ${extTotalUnits}`}</Button>
                  </div>
                  <Button data-testid="ext-cancel-session" variant="ghost" onClick={extResetSession} className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs">Cancel Session (nothing is saved)</Button>
                </div>
              )}
            </div>
          )}

          {/* ---------- MANUAL ---------- */}
          {phase === 'scan' && source === 'manual' && (
            <div className="space-y-4" data-testid="scanner-manual">
              <SwitchScannerBar />
              <ManualForm testid="barcode-manual-input" />
              <div className="flex gap-2">
                <Button data-testid="scanner-manual-use-camera" variant="outline" onClick={() => chooseSource('camera')} className="flex-1 border-[#262626] text-white hover:bg-[#262626]"><Camera className="w-4 h-4 mr-1" /> Use Camera</Button>
                <Button data-testid="scanner-manual-use-external" variant="outline" onClick={() => chooseSource('external')} className="flex-1 border-[#262626] text-white hover:bg-[#262626]"><Bluetooth className="w-4 h-4 mr-1" /> Bluetooth / USB</Button>
              </div>
            </div>
          )}

          {/* ---------- RESOLVED RESULT ---------- */}
          {phase === 'resolved' && item && (
            <div className="space-y-4" data-testid="barcode-resolved">
              <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
                <div className="flex gap-3">
                  {item.photo_file_id && (
                    <img data-testid="barcode-item-photo" src={`${INV_API}/items/${item.id}/photo?kind=item&v=${item.photo_file_id}`} alt={item.name}
                      className="w-20 h-20 shrink-0 rounded-md border border-[#262626] bg-[#0A0A0A] object-contain"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  )}
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-lg" data-testid="barcode-item-name">{item.name}</p>
                    <p className="text-[#A3A3A3] text-xs font-mono mt-0.5">Barcode {result.barcode}{item.sku ? ` · SKU ${item.sku}` : ''}{item.category ? ` · ${item.category}` : ''}</p>
                    {item.size && <p className="text-[#A3A3A3] text-xs mt-0.5" data-testid="barcode-item-size">Size: {item.size}</p>}
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#525252] mb-1">Current stock by location</p>
                  {(result.stock || []).length === 0 ? (
                    <p className="text-[#525252] text-sm">No stock recorded yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {result.stock.map((s) => (
                        <span key={s.location_id} className="bg-[#0A0A0A] border border-[#262626] rounded-full px-3 py-1 text-xs">
                          {s.location_name}: <span className="text-[#D4AF37] font-bold">{s.stock_basis === 'estimated' ? '~' : ''}{s.quantity}</span>
                          {s.stock_basis === 'estimated' && <span className="text-[10px] text-amber-300/80 ml-1">est.</span>}
                        </span>
                      ))}
                      <span data-testid="scan-lookup-exact-total" className="bg-[#0A0A0A] border border-[#262626] rounded-full px-3 py-1 text-xs text-green-400">Shop + WH: {result.company_total}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#525252] mb-2">Action</p>
                <div className="grid grid-cols-3 gap-2">
                  {ACTIONS.map((a) => {
                    const Icon = a.icon;
                    const active = action === a.key;
                    return (
                      <button key={a.key} data-testid={`barcode-action-${a.key}`} onClick={() => setAction(a.key)}
                        className={`flex flex-col items-center gap-1 rounded-md border p-2 text-xs transition-colors ${active ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#262626] bg-[#141414] hover:bg-[#1c1c1c]'}`}>
                        <Icon className={`w-4 h-4 ${a.color}`} />
                        <span className="text-white leading-tight text-center">{a.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {needsLoc && (
                <div className="space-y-1">
                  <Label className="text-white text-xs flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D4AF37]" /> {action === 'transfer' ? 'From location' : 'Current inventory location'}</Label>
                  <Select value={locId} onValueChange={(v) => { setLocId(v); const c = (result.stock || []).find((r) => r.location_id === v); setCounted(c ? String(c.quantity) : '0'); }}>
                    <SelectTrigger data-testid="barcode-location" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select a location…" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {locations.map((l) => <SelectItem key={l.id} value={l.id} className="text-white">{l.name} ({l.type})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {myLoc?.reliable && locId === myLoc.location.id && <p className="text-[10px] text-green-400">Defaulted to your assigned van: {myLoc.location.name}</p>}
                  {locMissing && <p className="text-[10px] text-amber-400">Select a location before saving — nothing is saved until confirmed.</p>}
                </div>
              )}

              {action === 'transfer' && (
                <div className="space-y-1">
                  <Label className="text-white text-xs flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-blue-400" /> To location</Label>
                  <Select value={toLocId} onValueChange={setToLocId}>
                    <SelectTrigger data-testid="barcode-to-location" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Destination…" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {locations.filter((l) => l.id !== locId).map((l) => <SelectItem key={l.id} value={l.id} className="text-white">{l.name} ({l.type})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {['use', 'receive', 'transfer', 'request_restock'].includes(action) && (
                <div className="space-y-1">
                  <Label className="text-white text-xs">Quantity</Label>
                  <Input data-testid="barcode-qty" type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              )}

              {action === 'count' && (
                <div className="space-y-1">
                  <Label className="text-white text-xs">Counted quantity {locId ? '(replaces on-hand at this location)' : ''}</Label>
                  <Input data-testid="barcode-counted" type="number" min="0" step="any" value={counted} onChange={(e) => setCounted(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              )}

              {action === 'request_restock' && (
                <div className="space-y-1">
                  <Label className="text-white text-xs">Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger data-testid="barcode-priority" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {['low', 'normal', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p} className="text-white capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {action !== 'lookup' && (
                <div className="space-y-1">
                  <Label className="text-white text-xs">Notes (optional)</Label>
                  <Input data-testid="barcode-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              )}

              {context.job_id && (
                <p className="text-[10px] text-[#D4AF37]">Job context attached — this movement will be linked to the active job.</p>
              )}

              <div className="flex gap-2 pt-1">
                <Button data-testid="barcode-rescan" variant="outline" onClick={rescan} className="border-[#262626] text-white hover:bg-[#262626]"><ScanBarcode className="w-4 h-4 mr-1" /> Scan another</Button>
                <Button data-testid="barcode-submit" onClick={submit} disabled={busy || (needsLoc && !locId)} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                  {action === 'lookup' ? 'Done' : busy ? 'Saving…' : 'Confirm'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
